import {
  noDecimalCurrencyFormatter,
  numberNoRoundingFormatter,
} from "@intelligems/ig-graphs/data-formatters";
import { DataType } from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import { StatSigData } from "@src/types/analytics-tinybird";

export const dataFormatWrapper = (
  value: number,
  dataType: DataType,
  storeCurrency: { code: string; locale: string }
) => {
  switch (dataType) {
    case "percentNoDecimal":
      return parseFloat(
        new Intl.NumberFormat(`${storeCurrency.locale}`, {
          style: "decimal",
          maximumFractionDigits: 0,
          minimumFractionDigits: undefined,
        })
          .format(value * 100)
          .replace(",", ".")
      );

    case "percentTwoDecimal":
      return parseFloat(
        new Intl.NumberFormat(`${storeCurrency.locale}`, {
          style: "decimal",
          maximumSignificantDigits: 3,
          minimumSignificantDigits: 1,
          minimumFractionDigits: 1,
        })
          .format(value * 100)
          .replace(",", ".")
      );
    case "percent":
      return parseFloat(
        new Intl.NumberFormat(`${storeCurrency.locale}`, {
          style: "decimal",
          maximumFractionDigits: 3,
          minimumFractionDigits: undefined,
        })
          .format(value * 100)
          .replace(",", ".")
      );
    default:
      return value;
  }
};

// dont multiple decimals
export const labelFormatWrapper = (
  value: number,
  dataType: DataType,
  storeCurrency: { code: string; locale: string },
  showThreeDecimals?: boolean
) => {
  switch (dataType) {
    case "percentTwoDecimal":
      return `${new Intl.NumberFormat(`${storeCurrency.locale}`, {
        style: "decimal",
        minimumFractionDigits: showThreeDecimals ? 3 : 1,
        maximumFractionDigits: showThreeDecimals ? 3 : 2,
      }).format(value)}%`;
    case "percentNoDecimal":
      return `${new Intl.NumberFormat(`${storeCurrency.locale}`, {
        style: "decimal",
        maximumFractionDigits: 0,
        minimumFractionDigits: undefined,
      }).format(value)}%`;
    case "percent":
      return `${new Intl.NumberFormat(`${storeCurrency.locale}`, {
        style: "decimal",
        maximumFractionDigits: 1,
        minimumFractionDigits: 1,
      }).format(value)}%`;
    case "currency":
      return `${new Intl.NumberFormat(storeCurrency.locale, {
        style: "currency",
        currency: storeCurrency.code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)}`;
    case "currencyNoDecimal":
      return noDecimalCurrencyFormatter(value, false, storeCurrency);
    case "numberNoRounding":
      return numberNoRoundingFormatter(value, false, storeCurrency);
    default:
      return `${new Intl.NumberFormat(`${storeCurrency.locale}`, {
        style: "decimal",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)}`;
  }
};

// add 10% to each side, but need to determine which is best for decimal vs currency
export const findMinCiRangeValue = (
  dataArr: StatSigData[],
  storeCurrency: { code: string; locale: string }
): number => {
  const allCiLow = dataArr
    .filter((data) => data.ci_range !== undefined)
    .map((data) => data.ci_range![0]);

  const lowest = Math.min(...allCiLow);
  const offset = lowest * 0.1;
  const combined = offset - lowest;
  const formatted = parseFloat(
    new Intl.NumberFormat(`${storeCurrency.locale}`, {
      style: "decimal",
      maximumSignificantDigits: 3,
      maximumFractionDigits: 1,
    })
      .format(combined)
      .replace(",", ".")
  );

  return Math.abs(formatted);
};

export const findMaxCiRangeValue = (
  dataArr: StatSigData[],
  storeCurrency: { code: string; locale: string }
): number => {
  if (dataArr.length === 0) {
    return 100;
  }

  const allCiHigh = dataArr
    .filter((data) => data.ci_range !== undefined)
    .map((data) => data.ci_range![1]);

  const highest = Math.max(...allCiHigh);
  const offset = highest * 0.1;
  const combined = offset + highest;
  const formatted = parseFloat(
    new Intl.NumberFormat(`${storeCurrency.locale}`, {
      style: "decimal",
      maximumSignificantDigits: 3,
      maximumFractionDigits: 1,
    })
      .format(combined)
      .replace(",", ".")
  );

  return Math.abs(formatted);
};
